// ═══════════════════════════════════════════════════════════════
//  Settup — Personality Calculation Engine
//  Models: Big Five (OCEAN), MBTI, Zodiac (≤10% weight)
//  Produces: Archetype label, dimension scores, match probability
// ═══════════════════════════════════════════════════════════════

// ─── Zodiac ───────────────────────────────────────────────────
export function getZodiacSign(birthday) {
  if (!birthday) return null;
  const d = new Date(birthday);
  if (isNaN(d)) return null;
  const m = d.getMonth() + 1, day = d.getDate();
  if ((m === 3 && day >= 21) || (m === 4 && day <= 19)) return "Aries";
  if ((m === 4 && day >= 20) || (m === 5 && day <= 20)) return "Taurus";
  if ((m === 5 && day >= 21) || (m === 6 && day <= 20)) return "Gemini";
  if ((m === 6 && day >= 21) || (m === 7 && day <= 22)) return "Cancer";
  if ((m === 7 && day >= 23) || (m === 8 && day <= 22)) return "Leo";
  if ((m === 8 && day >= 23) || (m === 9 && day <= 22)) return "Virgo";
  if ((m === 9 && day >= 23) || (m === 10 && day <= 22)) return "Libra";
  if ((m === 10 && day >= 23) || (m === 11 && day <= 21)) return "Scorpio";
  if ((m === 11 && day >= 22) || (m === 12 && day <= 21)) return "Sagittarius";
  if ((m === 12 && day >= 22) || (m === 1 && day <= 19)) return "Capricorn";
  if ((m === 1 && day >= 20) || (m === 2 && day <= 18)) return "Aquarius";
  return "Pisces";
}

const ZODIAC_ELEMENTS = {
  Aries: "Fire", Leo: "Fire", Sagittarius: "Fire",
  Taurus: "Earth", Virgo: "Earth", Capricorn: "Earth",
  Gemini: "Air", Libra: "Air", Aquarius: "Air",
  Cancer: "Water", Scorpio: "Water", Pisces: "Water",
};

const ZODIAC_MODALITIES = {
  Aries: "Cardinal", Cancer: "Cardinal", Libra: "Cardinal", Capricorn: "Cardinal",
  Taurus: "Fixed", Leo: "Fixed", Scorpio: "Fixed", Aquarius: "Fixed",
  Gemini: "Mutable", Virgo: "Mutable", Sagittarius: "Mutable", Pisces: "Mutable",
};

// Compatibility matrix: element pair → base score (0-100)
const ELEMENT_COMPAT = {
  "Fire-Fire": 80, "Fire-Air": 88, "Fire-Earth": 50, "Fire-Water": 45,
  "Earth-Earth": 82, "Earth-Water": 87, "Earth-Fire": 50, "Earth-Air": 55,
  "Air-Air": 78, "Air-Fire": 88, "Air-Earth": 55, "Air-Water": 60,
  "Water-Water": 80, "Water-Earth": 87, "Water-Fire": 45, "Water-Air": 60,
};

// Opposite sign bonus (complementary pairs are naturally drawn together)
const OPPOSITE_PAIRS = [
  ["Aries","Libra"], ["Taurus","Scorpio"], ["Gemini","Sagittarius"],
  ["Cancer","Capricorn"], ["Leo","Aquarius"], ["Virgo","Pisces"],
];

export function getZodiacCompatibility(sign1, sign2) {
  if (!sign1 || !sign2) return 70; // neutral if unknown
  if (sign1 === sign2) return 85; // same sign: strong resonance
  const el1 = ZODIAC_ELEMENTS[sign1], el2 = ZODIAC_ELEMENTS[sign2];
  const key = `${el1}-${el2}`;
  let score = ELEMENT_COMPAT[key] || 65;
  // Opposite pair bonus
  const isOpposite = OPPOSITE_PAIRS.some(
    ([a, b]) => (sign1 === a && sign2 === b) || (sign1 === b && sign2 === a)
  );
  if (isOpposite) score = Math.min(100, score + 8);
  // Same modality small bonus (similar life rhythms)
  if (ZODIAC_MODALITIES[sign1] === ZODIAC_MODALITIES[sign2]) score = Math.min(100, score + 4);
  return Math.round(score);
}

export const ZODIAC_TRAITS = {
  Aries:       { keywords: ["Bold", "Passionate", "Independent"], strengths: "Natural leader who dives in headfirst" },
  Taurus:      { keywords: ["Loyal", "Grounded", "Sensual"], strengths: "Deeply devoted and values stability" },
  Gemini:      { keywords: ["Witty", "Curious", "Adaptable"], strengths: "Keeps relationships exciting and mentally stimulating" },
  Cancer:      { keywords: ["Nurturing", "Intuitive", "Protective"], strengths: "Creates deep emotional bonds with genuine care" },
  Leo:         { keywords: ["Generous", "Confident", "Warm"], strengths: "Lights up every room and loves fiercely" },
  Virgo:       { keywords: ["Thoughtful", "Devoted", "Observant"], strengths: "Attentive to what their partner truly needs" },
  Libra:       { keywords: ["Harmonious", "Romantic", "Fair"], strengths: "Creates balance and prioritizes the relationship" },
  Scorpio:     { keywords: ["Intense", "Loyal", "Magnetic"], strengths: "Loves with unmatched depth and transforms through connection" },
  Sagittarius: { keywords: ["Adventurous", "Optimistic", "Honest"], strengths: "Brings freedom, laughter and big-picture vision" },
  Capricorn:   { keywords: ["Ambitious", "Reliable", "Patient"], strengths: "Builds something lasting — a true long-game partner" },
  Aquarius:    { keywords: ["Original", "Visionary", "Open-minded"], strengths: "Loves unconventionally and deeply respects autonomy" },
  Pisces:      { keywords: ["Empathic", "Creative", "Romantic"], strengths: "Connects on a soul level with rare emotional depth" },
};

// ─── Big Five (OCEAN) Calculation ─────────────────────────────
// Maps Stage 1 answers to preliminary Big Five scores (0–100 each).
// Full accuracy improves significantly after Stages 4-13.
export function calculateBigFive(answers) {
  // Defaults (population average = 50)
  let O = 50, C = 50, E = 50, A = 55, N = 45;

  // Openness: travel willingness, long-distance openness
  const travel = (answers.travel_distance || "").toLowerCase();
  if (travel.includes("long") || travel.includes("anywhere") || travel.includes("open")) O += 15;
  else if (travel.includes("city") || travel.includes("local")) O -= 8;

  // Openness: relationship intent (curious/adventurous = higher O)
  const intent = (answers.relationship_intent || "").toLowerCase();
  if (intent.includes("casual") || intent.includes("see where")) O += 8;
  if (intent.includes("long") || intent.includes("serious")) O -= 3;

  // Conscientiousness: single duration (long single = self-aware/choosy)
  const singleDuration = (answers.single_duration || "").toLowerCase();
  if (singleDuration.includes("year") || singleDuration.includes("few")) C += 8;

  // Extraversion: from relationship preferences and location density
  const location = (answers.location || "").toLowerCase();
  if (location.includes("york") || location.includes("angeles") || location.includes("london") || location.includes("miami")) E += 6;

  // Extraversion: dating gender preference breadth
  const genderPref = (answers.dating_gender_preference || "").toLowerCase();
  if (genderPref.includes("any") || genderPref.includes("open") || genderPref.includes("all")) { O += 8; E += 5; }

  // Agreeableness: long-term intent = higher A (stable relationship seeking)
  if (intent.includes("long") || intent.includes("serious") || intent.includes("marriage")) A += 12;
  if (intent.includes("casual")) A -= 5;

  // Neuroticism: recently single / divorced signals
  const status = (answers.relationship_status || "").toLowerCase();
  if (status.includes("divorced") || status.includes("separated")) N += 8;
  if (status.includes("widowed")) N += 6;
  const recentlySingle = singleDuration.includes("month") || singleDuration.includes("week");
  if (recentlySingle) N += 6;

  // Age-based calibrations (slight tendencies, not deterministic)
  const age = parseInt(answers.age) || 30;
  if (age >= 35) { C += 5; N -= 3; } // Older = more stable
  if (age <= 25) { O += 5; N += 3; } // Younger = more open but more anxious

  return {
    openness:          clamp(Math.round(O)),
    conscientiousness: clamp(Math.round(C)),
    extraversion:      clamp(Math.round(E)),
    agreeableness:     clamp(Math.round(A)),
    neuroticism:       clamp(Math.round(N)),
  };
}

// Enrich Big Five with later-stage answers
export function enrichBigFive(base, answers) {
  let { openness: O, conscientiousness: C, extraversion: E, agreeableness: A, neuroticism: N } = base;

  if (answers.weekend_lifestyle) {
    const wl = answers.weekend_lifestyle.toLowerCase();
    if (wl.includes("home") || wl.includes("quiet") || wl.includes("reading")) { E -= 10; C += 5; }
    if (wl.includes("out") || wl.includes("party") || wl.includes("social") || wl.includes("friends")) E += 12;
  }
  if (answers.fitness) {
    const fit = answers.fitness.toLowerCase();
    if (fit.includes("daily") || fit.includes("every day")) C += 10;
    if (fit.includes("never") || fit.includes("rarely")) C -= 5;
  }
  if (answers.conflict_resolution) {
    const cr = answers.conflict_resolution.toLowerCase();
    if (cr.includes("avoid") || cr.includes("space") || cr.includes("walk away")) N += 8;
    if (cr.includes("talk") || cr.includes("communicate") || cr.includes("directly")) A += 10;
  }
  if (answers.love_language) {
    const ll = answers.love_language.toLowerCase();
    if (ll.includes("words") || ll.includes("affirmation")) A += 6;
    if (ll.includes("acts") || ll.includes("service")) C += 8;
    if (ll.includes("touch") || ll.includes("physical")) E += 5;
  }
  if (answers.daily_rhythm) {
    const dr = answers.daily_rhythm.toLowerCase();
    if (dr.includes("morning")) C += 6;
    if (dr.includes("night") || dr.includes("owl")) O += 5;
  }
  if (answers.political_views) {
    const pv = answers.political_views.toLowerCase();
    if (pv.includes("liberal") || pv.includes("progressive")) O += 10;
    if (pv.includes("conservative")) C += 8;
  }

  return {
    openness:          clamp(Math.round(O)),
    conscientiousness: clamp(Math.round(C)),
    extraversion:      clamp(Math.round(E)),
    agreeableness:     clamp(Math.round(A)),
    neuroticism:       clamp(Math.round(N)),
  };
}

// ─── MBTI Derivation from Big Five ────────────────────────────
export function inferMBTI(bigFive) {
  const { openness: O, conscientiousness: C, extraversion: E, agreeableness: A, neuroticism: N } = bigFive;
  const IE = E >= 50 ? "E" : "I";
  const NS = O >= 55 ? "N" : "S";
  const TF = A >= 55 ? "F" : "T";
  const JP = C >= 55 ? "J" : "P";
  return `${IE}${NS}${TF}${JP}`;
}

export const MBTI_PROFILES = {
  INTJ: { name: "The Architect",     description: "Strategic, independent, and visionary. You love deeply but selectively." },
  INTP: { name: "The Thinker",       description: "Intellectually curious, analytical, and quietly passionate." },
  ENTJ: { name: "The Commander",     description: "Bold, decisive, and fiercely protective of those you love." },
  ENTP: { name: "The Debater",       description: "Witty, energetic, and captivating — you keep relationships alive with ideas." },
  INFJ: { name: "The Advocate",      description: "Rare, deep, and intensely empathic. You seek a true soul connection." },
  INFP: { name: "The Mediator",      description: "Idealistic romantic who loves wholeheartedly and sees the best in people." },
  ENFJ: { name: "The Protagonist",   description: "Charismatic, warm, and a natural at making people feel special." },
  ENFP: { name: "The Campaigner",    description: "Enthusiastic, creative, and overflowing with warmth and possibility." },
  ISTJ: { name: "The Logistician",   description: "Reliable, honest, and quietly devoted — a rock in any relationship." },
  ISFJ: { name: "The Defender",      description: "Warm, attentive, and deeply committed to those you love." },
  ESTJ: { name: "The Executive",     description: "Direct, dependable, and excellent at creating relationship stability." },
  ESFJ: { name: "The Consul",        description: "Social, caring, and skilled at nurturing lasting bonds." },
  ISTP: { name: "The Virtuoso",      description: "Calm, resourceful, and attractive in a quiet, understated way." },
  ISFP: { name: "The Adventurer",    description: "Gentle, spontaneous, and deeply in tune with your own heart." },
  ESTP: { name: "The Entrepreneur",  description: "Energetic, bold, and irresistibly fun to be around." },
  ESFP: { name: "The Entertainer",   description: "Vibrant, spontaneous, and the life of every room you walk into." },
};

// ─── Relationship Archetypes ───────────────────────────────────
export function getRelationshipArchetype(bigFive, intent) {
  const { openness: O, extraversion: E, agreeableness: A, conscientiousness: C } = bigFive;
  const isLongTerm = /long|serious|marriage|committed/i.test(intent || "");
  const isCasual = /casual|fun|see where/i.test(intent || "");

  if (A >= 65 && C >= 60 && isLongTerm)
    return { label: "The Devoted Partner", icon: "💛", description: "You're built for deep, lasting love. You show up fully and your loyalty is extraordinary." };
  if (O >= 65 && E >= 60 && isCasual)
    return { label: "The Free-Spirited Explorer", icon: "✨", description: "You bring excitement and spontaneity — every connection is an adventure with you." };
  if (A >= 60 && O >= 60 && isLongTerm)
    return { label: "The Soulful Connector", icon: "🌊", description: "You love deeply and intuitively. You don't just want a partner — you want a true companion." };
  if (C >= 65 && A >= 55)
    return { label: "The Thoughtful Nurturer", icon: "🌱", description: "You invest in relationships with intention and care. Partners feel genuinely seen with you." };
  if (E >= 65 && O >= 60)
    return { label: "The Magnetic Presence", icon: "⚡", description: "You have a rare energy that draws people in. Relationships with you are electric and alive." };
  if (O >= 65 && E <= 50)
    return { label: "The Quiet Visionary", icon: "🔭", description: "Depth over breadth — you seek a partner who can truly match your inner world." };
  if (E >= 60 && A >= 60)
    return { label: "The Warm Companion", icon: "☀️", description: "You make everyone around you feel at home. Your partner will always feel cherished." };
  return { label: "The Genuine Soul", icon: "💫", description: "Authentic, grounded, and refreshingly real. The right person will recognize your rare quality immediately." };
}

// ─── Full Profile Generator ────────────────────────────────────
export function generatePersonalityProfile(answers) {
  const bigFive = calculateBigFive(answers);
  const mbti = inferMBTI(bigFive);
  const mbtiProfile = MBTI_PROFILES[mbti] || MBTI_PROFILES["INFP"];
  const archetype = getRelationshipArchetype(bigFive, answers.relationship_intent);
  const zodiacSign = getZodiacSign(answers.birthday);
  const zodiacInfo = zodiacSign ? ZODIAC_TRAITS[zodiacSign] : null;
  const attachmentStyle = inferAttachmentStyle(answers, bigFive);

  return {
    bigFive,
    mbti,
    mbtiProfile,
    archetype,
    zodiacSign,
    zodiacInfo,
    attachmentStyle,
    completenessScore: Math.min(100, Object.keys(answers).length * 10),
  };
}

// ─── Attachment Style ──────────────────────────────────────────
export function inferAttachmentStyle(answers, bigFive) {
  const { neuroticism: N, agreeableness: A, extraversion: E } = bigFive;
  const status = (answers.relationship_status || "").toLowerCase();
  const duration = (answers.single_duration || "").toLowerCase();
  const intent = (answers.relationship_intent || "").toLowerCase();

  let secure = 50, anxious = 0, avoidant = 0, disorganized = 0;

  if (N <= 40 && A >= 55) secure += 20;
  if (N >= 60) { anxious += 20; secure -= 10; }
  if (E <= 40 && intent.includes("casual")) avoidant += 20;
  if (status.includes("divorced") || status.includes("separated")) { N > 50 ? anxious += 15 : avoidant += 10; }
  const recentlySingle = duration.includes("month") || duration.includes("week");
  if (recentlySingle && N > 50) anxious += 10;

  const max = Math.max(secure, anxious, avoidant, disorganized);
  if (max === secure) return { style: "Secure", description: "You approach relationships from a place of confidence and trust." };
  if (max === anxious) return { style: "Anxious-Preoccupied", description: "You care deeply and sometimes worry about connection — your passion is a gift." };
  if (max === avoidant) return { style: "Dismissive-Avoidant", description: "You value independence highly and take time to open up fully." };
  return { style: "Secure", description: "You approach relationships from a place of confidence and trust." };
}

// ─── Compatibility Score Calculator ───────────────────────────
// Weights: Personality 35% | Values 25% | Lifestyle 20% | Goals 10% | Astrology 10%
export function calculateCompatibilityScore(profileA, profileB) {
  const personality = bigFiveCompatibility(profileA.bigFive, profileB.bigFive);
  const goals = goalsCompatibility(profileA.answers, profileB.answers);
  const lifestyle = lifestyleCompatibility(profileA.answers, profileB.answers);
  const values = valuesCompatibility(profileA.answers, profileB.answers);
  const astrology = getZodiacCompatibility(profileA.zodiacSign, profileB.zodiacSign);

  const weighted =
    personality  * 0.35 +
    values       * 0.25 +
    lifestyle    * 0.20 +
    goals        * 0.10 +
    astrology    * 0.10;  // Astrology is exactly 10%

  return {
    total: Math.round(clamp(weighted)),
    breakdown: {
      personality:  Math.round(personality),
      values:       Math.round(values),
      lifestyle:    Math.round(lifestyle),
      goals:        Math.round(goals),
      astrology:    Math.round(astrology),
    },
    weights: { personality: 0.35, values: 0.25, lifestyle: 0.20, goals: 0.10, astrology: 0.10 },
  };
}

function bigFiveCompatibility(bf1, bf2) {
  const keys = ["openness","conscientiousness","extraversion","agreeableness","neuroticism"];
  // Some traits match better when similar, some when complementary
  const weights = { openness: 0.25, conscientiousness: 0.20, extraversion: 0.15, agreeableness: 0.25, neuroticism: 0.15 };
  let score = 0;
  for (const k of keys) {
    const diff = Math.abs((bf1[k] || 50) - (bf2[k] || 50));
    // High agreeableness match = very good; neuroticism: lower combined = better
    if (k === "neuroticism") {
      score += weights[k] * (100 - ((bf1[k] + bf2[k]) / 2));
    } else {
      score += weights[k] * (100 - diff * 0.8);
    }
  }
  return clamp(score * 100 / (Object.values(weights).reduce((a,b) => a+b, 0) * 100));
}

function goalsCompatibility(a, b) {
  if (!a || !b) return 70;
  const intent_a = (a.relationship_intent || "").toLowerCase();
  const intent_b = (b.relationship_intent || "").toLowerCase();
  const bothLong = /long|serious|marriage/.test(intent_a) && /long|serious|marriage/.test(intent_b);
  const bothCasual = /casual|fun/.test(intent_a) && /casual|fun/.test(intent_b);
  if (bothLong || bothCasual) return 95;
  if (intent_a && intent_b && intent_a !== intent_b) return 40;
  return 70;
}

function lifestyleCompatibility(a, b) {
  if (!a || !b) return 70;
  let score = 70;
  if (a.daily_rhythm && b.daily_rhythm) {
    const match = a.daily_rhythm.toLowerCase() === b.daily_rhythm.toLowerCase();
    score += match ? 15 : -10;
  }
  return clamp(score);
}

function valuesCompatibility(a, b) {
  if (!a || !b) return 70;
  let score = 70;
  if (a.religion && b.religion) {
    const bothReligious = /important|very|faith|believe/.test((a.religion + b.religion).toLowerCase());
    const bothNotReligious = /not|none|atheist|agnostic/.test((a.religion + b.religion).toLowerCase());
    if (bothReligious || bothNotReligious) score += 15;
  }
  if (a.political_views && b.political_views) {
    const sameViews = a.political_views.toLowerCase() === b.political_views.toLowerCase();
    score += sameViews ? 12 : -8;
  }
  return clamp(score);
}

function clamp(v) {
  return Math.min(100, Math.max(0, v));
}

// ─── Stage 1 Completeness & Encouragement ─────────────────────
export function getStage1Encouragement(profile) {
  const { mbti, mbtiProfile, archetype, zodiacSign, attachmentStyle, bigFive } = profile;
  const dominantTrait = getDominantTrait(bigFive);
  return {
    headline: `You're ${archetype.icon} ${archetype.label}`,
    tagline: archetype.description,
    mbtiLabel: `${mbti} — ${mbtiProfile.name}`,
    mbtiDescription: mbtiProfile.description,
    attachmentLabel: attachmentStyle.style,
    attachmentDescription: attachmentStyle.description,
    zodiacLabel: zodiacSign ? `${zodiacSign} ♡` : null,
    zodiacDescription: zodiacSign ? ZODIAC_TRAITS[zodiacSign]?.strengths : null,
    dominantTrait,
    teaser: buildTeaser(profile),
  };
}

function getDominantTrait(bigFive) {
  const map = {
    openness: "Openness to experience",
    conscientiousness: "Conscientiousness",
    extraversion: "Extraversion",
    agreeableness: "Warmth & agreeableness",
    neuroticism: "Emotional sensitivity",
  };
  const sorted = Object.entries(bigFive).sort((a, b) => b[1] - a[1]);
  return map[sorted[0][0]];
}

function buildTeaser(profile) {
  const { mbti, zodiacSign, attachmentStyle, bigFive } = profile;
  const lines = [];
  if (bigFive.agreeableness >= 60) lines.push("Your warmth is one of your most magnetic qualities.");
  if (bigFive.openness >= 65) lines.push("Your openness to new experiences makes you incredibly attractive to the right people.");
  if (bigFive.conscientiousness >= 65) lines.push("Your reliability and depth of commitment are rare — and deeply valued.");
  if (bigFive.extraversion >= 65) lines.push("Your social energy draws people in naturally.");
  if (attachmentStyle.style === "Secure") lines.push("Your secure attachment style is the foundation for a truly healthy relationship.");
  if (zodiacSign) lines.push(`As a ${zodiacSign}, you bring ${ZODIAC_TRAITS[zodiacSign]?.keywords?.join(", ")} energy to love.`);
  lines.push("Complete your full profile to unlock your relationship success score and your top matches.");
  return lines.slice(0, 3);
}
